<template>
  <SlideOver
    v-model="open"
    id="create-client-slide-over"
    :title="$t('inventory.clients.new-client')"
    :closeable="!loading"
    :loading-overlay="loading"
  >
    <Input
      v-model="editClient.name"
      id="name"
      :label="$t('general.name')"
      required
    />

    <Textarea
      v-model="editClient.description"
      id="description"
      :label="$t('general.description')"
    />

    <Select
      v-model="editClient.inventory"
      :auto-complete-collection="DATABASE_AUTOCOMPLETE_COLLECTION.Inventory"
      :query-params="{ rolloutId }"
      :label="$t('general.inventory')"
      :placeholder="$t('general.select-inventory-placeholder')"
      :clearable="false"
      :disabled="
        (preselectedInventory && isInventorySelectionDisabled) ||
          loadingInventory
      "
      required
    />

    <template v-if="!loadingInventory && populatedInventory">
      <CustomFieldSyncElement
        v-for="customField in requiredCustomFields"
        :key="customField._id"
        :model-value="customFieldValues[customField._id]"
        :custom-field="customField"
        @update:model-value="
          (newValue) => (customFieldValues[customField._id] = newValue)
        "
      />
    </template>
    <Spinner v-else-if="editClient.inventory" colored />

    <template #footer>
      <div class="flex flex-auto items-center justify-between space-x-2">
        <Checkbox
          v-model="keepSlideOverOpened"
          id="keep-slide-over-opened"
          label="Create another client"
        />

        <div class="flex space-x-2">
          <Button
            type="secondary"
            size="lg"
            :disabled="loading"
            @click="cancel"
          >
            {{ $t('general.cancel') }}
          </Button>
          <Button
            size="lg"
            :loading="loading"
            :disabled="!changed || !populatedInventory"
           
            @click="saveClient"
          >
            {{ $t('inventory.clients.create-client') }}
          </Button>
        </div>
      </div>
    </template>
  </SlideOver>
</template>

<script lang="ts">
export type CreateClient = Pick<Client, 'inventory' | 'name' | 'description'>;

export const defaultClient: CreateClient = {
  inventory: undefined as unknown as Inventory,
  name: '',
  description: '',
};
</script>

<script lang="ts" setup>
import { isEqual } from 'lodash';
import { klona } from 'klona';
import { TRPCClientError } from '@trpc/client';
import { DATABASE_AUTOCOMPLETE_COLLECTION } from '@/types';
import type {
  Client,
  CustomField,
  Inventory,
  Rollout,
  CustomFieldValues,
} from '@/types';
import { ErrorType } from '@/types/error';

import { useNotifications } from '@/stores/notifications';

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  preselectedInventory: {
    type: Object as PropType<Inventory>,
  },
  isInventorySelectionDisabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'updated']);

const open = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const keepSlideOverOpened = ref(false);

const editClient = ref<CreateClient>(klona(defaultClient));

const { selectedRollout, getRolloutRelativeLink } = useRolloutRouting();
const rolloutId = computed(() => (selectedRollout.value as Rollout)._id);

// Displaying custom fields that are required for the selected inventory
const populatedInventory = ref<Inventory>();
const customFieldValues = ref<CustomFieldValues>({});

const requiredCustomFields = computed(() => {
  return (
    populatedInventory.value?.customFieldGroups?.reduce((res, group) => {
      return [...res, ...group.customFields.filter((field) => field.required)];
    }, [] as CustomField[]) ?? []
  );
});

const changed = computed(() => !isEqual(defaultClient, editClient.value));

const loading = ref(false);

const cancel = () => {
  if (!loading.value) {
    open.value = false;
  }
};

const resetClient = () => {
  editClient.value = klona(defaultClient);
  customFieldValues.value = {};

  loading.value = false;

  if (props.preselectedInventory) {
    editClient.value.inventory = props.preselectedInventory;
  }
};

watch(
  computed(() => props.modelValue),
  (newValue) => {
    if (newValue) {
      keepSlideOverOpened.value = false;
      resetClient();
    }
  }
);

watch(
  computed(() => editClient.value.inventory),
  (newValue) => {
    if (newValue) {
      if (newValue.customFieldGroups) {
        populatedInventory.value = newValue;
      } else if (
        !populatedInventory.value ||
        populatedInventory.value._id !== newValue._id
      ) {
        fetchInventory(newValue._id);
      }
    } else {
      populatedInventory.value = undefined;
    }
  }
);

// Requests

const { $client } = useNuxtApp();
const notificationsStore = useNotifications();
const i18n = useI18n();

const loadingInventory = ref(false);

const fetchInventory = async (inventoryId: string) => {
  if (loadingInventory.value) {
    return;
  }

  loadingInventory.value = true;
  try {
    const { item: inventory } = await $client.inventory.getById.query({
      _id: inventoryId,
    });

    customFieldValues.value = {};
    populatedInventory.value = inventory;
  } catch (error) {
    useErrorHandler(error);
  } finally {
    loadingInventory.value = false;
  }
};

const isClientFormValid = () => {
  if (!editClient.value?.name?.length || !editClient.value?.inventory?._id) {
    notificationsStore.createIncompleteNotification();
    return false;
  }

  if (
    requiredCustomFields.value.length &&
    requiredCustomFields.value.some(
      (field) => !customFieldValues.value[field._id]?.length
    )
  ) {
    notificationsStore.createIncompleteNotification();
    return false;
  }

  return true;
};

const saveClient = async () => {
  if (loading.value || !changed.value || !isClientFormValid()) {
    return;
  }

  loading.value = true;
  try {
    const result = await $client.client.create.mutate({
      name: editClient.value.name,
      description: editClient.value.description,
      inventory: editClient.value.inventory._id,
      customFieldValues: customFieldValues.value,
    });

    const client = result.item as unknown as Client;

    emit('updated');

    notificationsStore.createNotification({
      type: 'success',
      title: client.name,
      message: i18n.t('settings.client.created-message'),
      link: getRolloutRelativeLink('/inventory/clients/view/' + client._id),
    });

    if (keepSlideOverOpened.value) {
      resetClient();
    } else {
      open.value = false;
    }
  } catch (err) {
    loading.value = false;

    const errorMap = [
      {
        type: ErrorType.CLIENT_LIMIT_EXCEEDED,
        message: 'settings.client.error.client-limit-exceeded-message',
        title: 'settings.client.error.client-limit-exceeded-title',
      },
      {
        type: ErrorType.NAME_ALREADY_EXISTS,
        message: 'settings.client.error.client-name-already-exists-message',
        title: 'settings.client.error.client-name-already-exists-title',
      },
      {
        type: ErrorType.NAME_IS_MANDATORY,
        message: 'settings.client.error.client-name-is-mandatory-message',
        title: 'settings.client.error.client-name-is-mandatory-title',
      },
      {
        type: ErrorType.NOT_VALID,
        message: 'error-messages.invalid-value',
      },
    ];

    const message =
      errorMap.find(
        (item) =>
          err instanceof TRPCClientError &&
          (item.type === err.message || err.data?.zodError?.includes(item.type))
      )?.message ?? 'error-messages.something-went-wrong';

    const title =
      errorMap.find(
        (item) =>
          err instanceof TRPCClientError &&
          (item.type === err.message || err.data?.zodError?.includes(item.type))
      )?.title ?? 'settings.client.error.client-create-error-title';

    useErrorHandler(err, {
      type: 'error',
      title: i18n.t(title),
      message: i18n.t(message),
    });
  }
};
</script>
